import React, { useEffect, useRef, useCallback } from 'react';
import { Box, HStack, Text, IconButton, Badge, Tooltip } from '@chakra-ui/react';
import { FiX, FiMaximize2, FiMinimize2 } from 'react-icons/fi';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { WebLinksAddon } from '@xterm/addon-web-links';
import '@xterm/xterm/css/xterm.css';

/**
 * ContainerTerminal – renders an interactive shell session inside a
 * Kubernetes container.  Communicates with the Django Channels
 * ExecConsumer over a WebSocket.
 *
 * Props:
 *   podName       – K8s pod name
 *   containerName – container inside the pod
 *   onClose       – callback when the user closes the terminal
 *   isFullscreen  – (optional) start in fullscreen mode
 */
function ContainerTerminal({ podName, containerName, onClose, isFullscreen: initialFullscreen = false }) {
  const termRef = useRef(null);
  const xtermRef = useRef(null);
  const wsRef = useRef(null);
  const fitAddonRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = React.useState(initialFullscreen);
  const [connected, setConnected] = React.useState(false);

  const sendResize = useCallback(() => {
    const term = xtermRef.current;
    const ws = wsRef.current;
    if (!term || !ws || ws.readyState !== WebSocket.OPEN) return;

    const fitAddon = fitAddonRef.current;
    if (fitAddon) {
      try { fitAddon.fit(); } catch (e) { /* ignore */ }
    }

    const msg = '\x00' + JSON.stringify({
      type: 'resize',
      cols: term.cols,
      rows: term.rows,
    });
    ws.send(msg);
  }, []);

  useEffect(() => {
    if (!termRef.current) return;

    // Create terminal
    const term = new Terminal({
      cursorBlink: true,
      cursorStyle: 'block',
      fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', Menlo, Monaco, 'Courier New', monospace",
      fontSize: 13,
      lineHeight: 1.2,
      theme: {
        background: '#1a1b26',
        foreground: '#a9b1d6',
        cursor: '#c0caf5',
        selectionBackground: '#33467c',
        black: '#32344a',
        red: '#f7768e',
        green: '#9ece6a',
        yellow: '#e0af68',
        blue: '#7aa2f7',
        magenta: '#ad8ee6',
        cyan: '#449dab',
        white: '#787c99',
        brightBlack: '#444b6a',
        brightRed: '#ff7a93',
        brightGreen: '#b9f27c',
        brightYellow: '#ff9e64',
        brightBlue: '#7da6ff',
        brightMagenta: '#bb9af7',
        brightCyan: '#0db9d7',
        brightWhite: '#acb0d0',
      },
      scrollback: 10000,
      convertEol: true,
      allowProposedApi: true,
    });

    const fitAddon = new FitAddon();
    const webLinksAddon = new WebLinksAddon();

    term.loadAddon(fitAddon);
    term.loadAddon(webLinksAddon);

    term.open(termRef.current);

    xtermRef.current = term;
    fitAddonRef.current = fitAddon;

    // Initial fit
    setTimeout(() => {
      try { fitAddon.fit(); } catch (e) { /* ignore */ }
    }, 100);

    // Connect WebSocket
    const token = localStorage.getItem('token');
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const wsUrl = `${proto}://${window.location.host}/ws/exec/${encodeURIComponent(podName)}/${encodeURIComponent(containerName)}/?token=${token}`;

    term.writeln(`\x1b[90m──── Connecting to ${containerName} in ${podName} ────\x1b[0m`);
    term.writeln('');

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      // Send initial size
      const msg = '\x00' + JSON.stringify({
        type: 'resize',
        cols: term.cols,
        rows: term.rows,
      });
      ws.send(msg);
    };

    ws.onmessage = (event) => {
      term.write(event.data);
    };

    ws.onclose = (event) => {
      setConnected(false);
      if (event.code === 4001) {
        term.writeln('\r\n\x1b[31mAuthentication required. Please log in again.\x1b[0m');
      } else if (event.code === 4003) {
        term.writeln('\r\n\x1b[31mInvalid token. Please log in again.\x1b[0m');
      } else {
        term.writeln('\r\n\x1b[33m──── Connection closed ────\x1b[0m');
      }
    };

    ws.onerror = () => {
      term.writeln('\r\n\x1b[31mWebSocket error. Check your connection.\x1b[0m');
    };

    // Forward keystrokes to WebSocket
    term.onData((data) => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(data);
      }
    });

    // Handle binary data (for things like ctrl+c)
    term.onBinary((data) => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(data);
      }
    });

    // Resize observer
    const resizeObserver = new ResizeObserver(() => {
      setTimeout(() => {
        try { fitAddon.fit(); } catch (e) { /* ignore */ }
        if (ws.readyState === WebSocket.OPEN) {
          const resizeMsg = '\x00' + JSON.stringify({
            type: 'resize',
            cols: term.cols,
            rows: term.rows,
          });
          ws.send(resizeMsg);
        }
      }, 50);
    });
    resizeObserver.observe(termRef.current);

    // Cleanup
    return () => {
      resizeObserver.disconnect();
      ws.close();
      term.dispose();
      xtermRef.current = null;
      wsRef.current = null;
      fitAddonRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [podName, containerName]);

  // Re-fit on fullscreen toggle
  useEffect(() => {
    setTimeout(sendResize, 150);
  }, [isFullscreen, sendResize]);

  const containerStyle = isFullscreen
    ? {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        bg: '#1a1b26',
        display: 'flex',
        flexDirection: 'column',
      }
    : {
        bg: '#1a1b26',
        borderRadius: 'lg',
        overflow: 'hidden',
        boxShadow: 'dark-lg',
        display: 'flex',
        flexDirection: 'column',
        height: '450px',
      };

  return (
    <Box {...containerStyle}>
      {/* Terminal title bar */}
      <HStack
        bg="#24283b"
        px={3}
        py={1.5}
        spacing={2}
        justify="space-between"
        borderBottom="1px solid"
        borderColor="whiteAlpha.100"
        flexShrink={0}
      >
        <HStack spacing={2}>
          {/* Traffic light dots */}
          <HStack spacing={1.5}>
            <Box w={3} h={3} borderRadius="full" bg="red.400" cursor="pointer" onClick={onClose} />
            <Box w={3} h={3} borderRadius="full" bg="yellow.400" cursor="pointer" onClick={() => setIsFullscreen(f => !f)} />
            <Box w={3} h={3} borderRadius="full" bg="green.400" cursor="pointer" onClick={() => setIsFullscreen(f => !f)} />
          </HStack>
          <Text fontSize="xs" color="whiteAlpha.700" fontFamily="mono" ml={2}>
            {containerName}@{podName}
          </Text>
        </HStack>
        <HStack spacing={1}>
          <Badge
            colorScheme={connected ? 'green' : 'red'}
            fontSize="2xs"
            variant="subtle"
          >
            {connected ? 'CONNECTED' : 'DISCONNECTED'}
          </Badge>
          <Tooltip label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
            <IconButton
              icon={isFullscreen ? <FiMinimize2 /> : <FiMaximize2 />}
              size="xs"
              variant="ghost"
              color="whiteAlpha.700"
              _hover={{ color: 'white', bg: 'whiteAlpha.200' }}
              onClick={() => setIsFullscreen(f => !f)}
              aria-label="Toggle fullscreen"
            />
          </Tooltip>
          <Tooltip label="Close terminal">
            <IconButton
              icon={<FiX />}
              size="xs"
              variant="ghost"
              color="whiteAlpha.700"
              _hover={{ color: 'red.300', bg: 'whiteAlpha.200' }}
              onClick={onClose}
              aria-label="Close terminal"
            />
          </Tooltip>
        </HStack>
      </HStack>

      {/* Terminal viewport */}
      <Box
        ref={termRef}
        flex="1"
        p={1}
        sx={{
          '& .xterm': { height: '100%' },
          '& .xterm-viewport': { overflow: 'hidden !important' },
        }}
      />
    </Box>
  );
}

export default ContainerTerminal;
